import React, { useState, useEffect } from "react";
import { UploadOutlined } from "@ant-design/icons";
import {
  Form,
  Input,
  Upload,
  Select,
  Modal,
  Button,
  Space,
  Row,
  Col,
  DatePicker,
  Table,
  InputNumber,
} from "antd";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { apiRequest } from "../../../../service/apiRequest";
import { showSuccess, showError } from "../../../../components/errorHandler";
// import type {
//     CompanyName,
// } from '../../../../types/CompanyName';
import type { UploadFile } from 'antd/es/upload/interface';
import type {
  Pn,
  Categories,
  BrandNames,
} from "../../../../types/CreateProductType";
const { Option } = Select;

interface AccountHead {
  ah_id: number;
  ah_name: string;
  ah_parent: number;

}
interface SubHead {
  ah_id: number;
  ah_name: string;
}

interface LinkItem {
  link: string;
  description?: string;
}
interface BankItem {
  bank_name: string;
  ifsc: string;
  acc_no: string;
  address?: string;
  holder_name?: string;
}
interface AddressItem {
  type?: "residence" | "office";
  address?: string;
  landmark?: string;
  pincode?: string;
  city?: string;
  state?: string;
  country?: string;
}
interface PhoneItem {
  first_name?: string; 
  last_name?: string; 
  label?: string; 
  number?: string; 
  designation?: string; 
  email?: string; 
  std_phone?: string;
  type?: string;
  std_code?: string;
}


interface CategoryItem {
  category_id?: number;
  discount?: string;
}
interface DocumentItem {
  document_id?: number;
  document_type: string;
  document_name: string;
  attachments: string;
}

interface LedgerFormValues {
  ledger_first_name: string;
  ledger_last_name?: string;
  ledger_account_head?: number;
  ledger_sub_account_head?: number;
  ledger_mobile_no?: string;
  ledger_company_name?: string;
  ledger_occupation?: string;
  ledger_designation?: string;
  ledger_opening_balance?: number;
  ledger_opening_type?: string;
  ledger_email?: string;
  ledger_gstin?: string;
  ledger_pan_no?: string;
  ledger_cin_no?: string;
  ledger_tin_no?: string;
  ledger_msme_reg_no?: string;
  ledger_bill_to_bill?: string;
  ledger_deals_in_company?: string[];
  ledger_deals_in_product?: string[];
  ledger_interest_rate?: number;
  ledger_interest_style?: string;
  ledger_credit_limit_day?: number;
  ledger_credit_limit_amount?: number;
  ledger_dob?: dayjs.Dayjs | null;
  ledger_marriage_anniversary?: dayjs.Dayjs | null;
  ledger_work_anniversary?: dayjs.Dayjs | null;
  ledger_offer_festival?: string;
  ledger_status?: number;
  ledger_image?: string;
  links?: LinkItem[];
  banks?: BankItem[];
  addresses?: AddressItem[];
  phones?: PhoneItem[];
  categories?: CategoryItem[];
  documents?: DocumentItem[];
}

interface LedgerModalProps {
  visible: boolean;
  onCancel: () => void;
  onCreate: (data: any) => void;
  initialValues?: (LedgerFormValues & { ledger_id?: number }) | null;
  isEditMode?: boolean;
}

const LedgerModal: React.FC<LedgerModalProps> = ({
  visible,
  onCancel,
  onCreate,
  initialValues = null,
  isEditMode = false,
}) => {
  const [form] = Form.useForm<LedgerFormValues>();
  const [accountHeads, setAccountHeads] = useState<AccountHead[]>([]);
  // const [companies, setCompany] = useState<CompanyName[]>([]);
  const [product_names, setProductName] = useState<Pn[]>([]);
  const [subHeads, setSubHeads] = useState<SubHead[]>([]);
  const [categories, setCategories] = useState<Categories[]>([]);
  const [discounts, setDiscounts] = useState<Record<string, string>>({});
  const [brandnames, setBrandNames] = useState<BrandNames[]>([]);

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [profileImageFileList, setProfileImageFileList] = useState<UploadFile[]>([]);


  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');

  const handlePincodeBlur = async (pincode: string, name: number) => {
    if (!/^\d{6}$/.test(pincode)) return;

    try {
      const data = await apiRequest<{
        city: string;
        state: string;
        country: string;
      }>({
        method: "get",
        url: `lookup/pincode/${pincode}`, // change URL to your actual API endpoint
      });

      const currentAddresses = form.getFieldValue("addresses") || [];
      const updatedAddresses = [...currentAddresses];
      updatedAddresses[name] = {
        ...updatedAddresses[name],
        city: data.city,
        state: data.state,
        country: data.country,
      };

      form.setFieldsValue({ addresses: updatedAddresses });
    } catch {
      showError("Could not fetch location data");
    }
  };

  useEffect(() => {
    apiRequest<AccountHead[]>({ method: "get", url: `account-heads?status=1&ah_parent=0` })
      .then((resp) => setAccountHeads(resp))
      .catch(() => showError("Failed to load account heads"));

    apiRequest<SubHead[]>({ method: "get", url: `account-heads?status=1` })
      .then((resp) => setSubHeads(resp))
      .catch(() => showError("Failed to load sub heads"));

    apiRequest<BrandNames[]>({ method: "get", url: `brand?status=1` })
      .then((resp) => setBrandNames(resp))
      .catch(() => showError("Failed to load company name "));

    apiRequest<Pn[]>({ method: "get", url: `product-names?status=1` })
      .then((resp) => setProductName(resp))
      .catch(() => showError("Failed to load product names"));
  }, []);

  useEffect(() => {
    if (visible) {
      if (isEditMode && initialValues) {
        const values: LedgerFormValues = {
          ...initialValues,
          ledger_dob: initialValues.ledger_dob
            ? dayjs(initialValues.ledger_dob)
            : null,
          ledger_marriage_anniversary: initialValues.ledger_marriage_anniversary
            ? dayjs(initialValues.ledger_marriage_anniversary)
            : null,
          ledger_work_anniversary: initialValues.ledger_work_anniversary
            ? dayjs(initialValues.ledger_work_anniversary)
            : null,
          links: initialValues.links || [],
          banks: initialValues.banks || [],
          phones: initialValues.phones || [],
          categories: initialValues.categories || [],
          addresses: initialValues.addresses || [],
          ledger_deals_in_product: (() => {
            const raw = initialValues.ledger_deals_in_product;
            if (Array.isArray(raw)) return raw;
            if (typeof raw === "string") {
              try {
                const parsed = JSON.parse(raw);
                return Array.isArray(parsed) ? parsed : [];
              } catch {
                return [];
              }
            }
            return [];
          })(),
          ledger_deals_in_company: (() => {
            const raw = initialValues.ledger_deals_in_company;
            if (Array.isArray(raw)) return raw;
            if (typeof raw === "string") {
              try {
                const parsed = JSON.parse(raw);
                return Array.isArray(parsed) ? parsed : [];
              } catch {
                return [];
              }
            }
            return [];
          })(),
        };
        if (initialValues.ledger_image) {
            const imageUrl = `${initialValues.ledger_image}`;
            setProfileImageFileList([
                {
                    uid: '-1',
                    name: 'image.png',
                    status: 'done',
                    url: imageUrl,
                },
            ]);
            setImageFile(null); // prevent old file being reused
            } else {
            setProfileImageFileList([]); // <- ✅ clear if no image
            setImageFile(null);
          }
          form.setFieldsValue(values);
      } else {
        form.resetFields();
        setImageFile(null); // ✅ clear uploaded file
        setProfileImageFileList([]); 
      }
    }
  }, [visible, isEditMode, initialValues, form]);

  const onFinish = async (values: LedgerFormValues) => {
    try {
      console.log("Form submit values:", values);

      const formData = new FormData();
      if (initialValues?.ledger_id) {
        formData.append("ledger_id", initialValues.ledger_id.toString());
      }
      // Ledger fields
      formData.append("ledger_first_name", values.ledger_first_name || "");
      formData.append("ledger_last_name", values.ledger_last_name || "");
      formData.append("ledger_mobile_no", values.ledger_mobile_no || "");
      formData.append("ledger_company_name", values.ledger_company_name || "");
      formData.append("ledger_occupation", values.ledger_occupation || "");
      formData.append("ledger_image", values.ledger_image || "");
      formData.append("ledger_designation", values.ledger_designation || "");
      formData.append(
          "ledger_opening_balance",
          (values.ledger_opening_balance ?? "").toString()
        );
      formData.append("ledger_opening_type", values.ledger_opening_type || "");
      formData.append("ledger_email", values.ledger_email || "");
      formData.append("ledger_gstin", values.ledger_gstin || "");
      formData.append("ledger_pan_no", values.ledger_pan_no || "");
      formData.append("ledger_tin_no", values.ledger_tin_no || "");
      formData.append("ledger_cin_no", values.ledger_cin_no || "");
      formData.append("ledger_msme_reg_no", values.ledger_msme_reg_no || "")
      formData.append(
          "ledger_interest_rate",
          (values.ledger_interest_rate ?? "").toString()
        );
      formData.append("ledger_interest_style", values.ledger_interest_style || "");
      formData.append(
        "ledger_credit_limit_day",
        (values.ledger_credit_limit_day ?? "").toString()
      );
      formData.append(
        "ledger_credit_limit_amount",
        (values.ledger_credit_limit_amount ?? "").toString()
      );
      // formData.append("ledger_interest_rate", values.ledger_interest_rate || "");
      // formData.append("ledger_credit_limit_day", values.ledger_credit_limit_day || "");
      // formData.append("ledger_credit_limit_amount", values.ledger_credit_limit_amount || "");
      formData.append("ledger_offer_festival", values.ledger_offer_festival || "");
      if (imageFile) {
              formData.append('attachments', imageFile); // ✅ binary file
          }

      formData.append(
        "ledger_deals_in_company",
        Array.isArray(values.ledger_deals_in_company)
          ? JSON.stringify(values.ledger_deals_in_company)
          : "[]"
      );

      formData.append(
        "ledger_deals_in_product",
        Array.isArray(values.ledger_deals_in_product)
          ? JSON.stringify(values.ledger_deals_in_product)
          : "[]"
      );

      // Dates
      if (values.ledger_dob) {
        formData.append("ledger_dob", values.ledger_dob.toISOString());
      }
      if (values.ledger_marriage_anniversary) {
        formData.append(
          "ledger_marriage_anniversary",
          values.ledger_marriage_anniversary.toISOString()
        );
      }
      if (values.ledger_work_anniversary) {
        formData.append(
          "ledger_work_anniversary",
          values.ledger_work_anniversary.toISOString()
        );
      }
      console.log("document data", values);
      // Multiple documents
      values.documents?.forEach((doc, index) => {
        formData.append(
          `documents[${index}][doc_type]`,
          doc.document_type
        );
        formData.append(
          `documents[${index}][doc_name]`,
          doc.document_name
        );

        

        if (doc.attachments && Array.isArray(doc.attachments)) {
          doc.attachments.forEach((file: any, fIndex: number) => {
            if (file?.originFileObj) {
              // send the actual file
              formData.append(
                `documents[${index}][doc_file][${fIndex}]`,
                file.originFileObj as File
              );
            } else if (file instanceof File) {
              // in case it's already a File
              formData.append(
                `documents[${index}][doc_file][${fIndex}]`,
                file
              );
            }
          });
        }
      });

      // phone
      values.phones?.forEach((ph, index) => {
        const contactType = ph.type ?? ""; 

        formData.append(`phones[${index}][first_name]`, ph.first_name ?? "");
        formData.append(`phones[${index}][last_name]`, ph.last_name ?? "");
        formData.append(`phones[${index}][label]`, ph.label ?? "");
        formData.append(`phones[${index}][designation]`, ph.designation ?? "");
        formData.append(`phones[${index}][email]`, ph.email ?? "");
        formData.append(`phones[${index}][type]`, contactType);

        if (contactType === "mobile") {
          formData.append(`phones[${index}][number]`, ph.number?.toString() ?? "");
          formData.append(`phones[${index}][std_code]`, ""); // clear std_code
          formData.append(`phones[${index}][phone_no]`, ""); // clear phone_no
          formData.append(`phones[${index}][std_phone]`, ""); // clear std_phone
        } else if (contactType === "phone") {
          const stdCode = ph.std_code?.toString() ?? "";
          formData.append(`phones[${index}][std_code]`, stdCode);
          // formData.append(`phones[${index}][phone_no]`, phoneNo);
          // formData.append(`phones[${index}][number]`, ""); // mobile field empty
          // formData.append(
          //   `phones[${index}][std_phone]`,
          //   stdCode && phoneNo ? stdCode + phoneNo : ""
          // );
          formData.append(`phones[${index}][std_phone]`, ph.std_phone ?? "");

        }
      });

      // links
      values.links?.forEach((l, index) => {
        formData.append(`links[${index}][link]`, l.link ?? "");
        formData.append(`links[${index}][description]`, l.description ?? "");
      });

      // banks
      values.banks?.forEach((b, index) => {
        formData.append(`banks[${index}][ifsc]`, b.ifsc ?? "");
        formData.append(`banks[${index}][bank_name]`, b.bank_name ?? "");
        formData.append(`banks[${index}][address]`, b.address ?? "");
        formData.append(`banks[${index}][acc_no]`, b.acc_no ?? "");
        formData.append(`banks[${index}][holder_name]`, b.holder_name ?? "");
      });

      // address
      values.addresses?.forEach((a, index) => {
        formData.append(`addresses[${index}][type]`, a.type ?? "");
        formData.append(`addresses[${index}][address]`, a.address ?? "");
        formData.append(`addresses[${index}][landmark]`, a.landmark ?? "");
        formData.append(`addresses[${index}][pincode]`, a.pincode ?? "");
        formData.append(`addresses[${index}][city]`, a.city ?? "");
        formData.append(`addresses[${index}][state]`, a.state ?? "");
        formData.append(`addresses[${index}][country]`, a.country ?? "");
      });

      values.categories?.forEach((cat, index) => {
        formData.append(`categories[${index}][category_id]`, String(cat.category_id ?? ""));
        formData.append(`categories[${index}][discount]`, cat.discount ?? "");
      });




      const method = isEditMode ? "put" : "post";
      const url = isEditMode ? "edit-ledger" : "add-ledger";

      const resp = await apiRequest<any>({
        method,
        url,
        data: formData,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      showSuccess(isEditMode ? "Ledger updated!" : "Ledger created!");
      form.resetFields();
      if (!visible) { //check this
              setImageFile(null);
              setProfileImageFileList([]);
          }
      onCreate(resp);
      onCancel();
    } catch (err) {
      console.error(err);
      showError(isEditMode ? "Update failed" : "Create failed");
    }
  };

  const fetchcategory = async () => {
    try {
      const data = await apiRequest<Categories[]>({
        method: "get",
        url: `category?status=1`,
      });
      //   setCategories(data);
      if (data && data.length) {
        form.setFieldsValue({
          categories: data.map((cat) => ({
            category_id: cat.category_id,
            category_name: cat.category_name,
            discount: "",
          })),
        });
        setCategories(data); // state को भी अपडेट करें
      }
    } catch (error) {
      showError("Failed to fetch category");
      console.error(error);
    }
  };

  useEffect(() => {
    fetchcategory();
  }, []);

  const handleDiscountChange = (categoryId: number, value: string) => {
    setDiscounts((prev) => ({
      ...prev,
      [categoryId]: value,
    }));
  };

  // image preview 
  const handlePreview = async (file: UploadFile) => {
      if (!file.url && !file.preview) {
          file.preview = await getBase64(file.originFileObj as File);
      }

      setPreviewImage(file.url || (file.preview as string));
      setPreviewOpen(true);
      setPreviewTitle(file.name || file.url!.substring(file.url!.lastIndexOf('/') + 1));
  };

  // Utility function to convert image to base64
  const getBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = error => reject(error);
    });

  const uploadButton = (
    <div
      style={{
          width: 220,
          height: 220,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
      }}
    >
        <PlusOutlined style={{ fontSize: 24 }} />
        <div style={{ marginTop: 8 }}>Upload</div>
    </div>
  );
  
  

  const columns = [
    {
      title: "Category Name",
      dataIndex: "category_name",
      key: "category_name",
    },
    {
      title: "Discount",
      key: "discount",
      render: (_: any, record: CategoryItem, index: number) => (
        <>
          <Form.Item
            name={["categories", index, "discount"]}
            rules={[
              {
                validator: (_, value) => {
                  if (value === undefined || value === "")
                    return Promise.resolve();
                  if (value >= 0 && value <= 100) return Promise.resolve();
                  return Promise.reject(
                    new Error("Please enter a value between 0 and 100")
                  );
                },
              },
            ]}
            style={{ marginBottom: 0 }}
          >
            <InputNumber
              placeholder="Enter discount"
              step={0.01}
              min={0}
              max={100}
              maxLength={6}
              onChange={(val) => {
                if (val === null || val === undefined) return;
                const clamped = Math.min(100, Math.max(0, val));
                const categories = form.getFieldValue("categories") || [];
                categories[index] = { ...categories[index], discount: clamped };
                form.setFieldsValue({ categories });
              }}
              style={{
                width: "100%",
                // borderRadius: "20px",
                // height: "40px"
              }}
              className="input-bordor-radius-with-border"
            />
          </Form.Item>

          <Form.Item
            name={["categories", index, "category_id"]}
            initialValue={record.category_id} // pass from your categories data source
            hidden={true} // AntD 4.16+ supports hidden prop instead of custom style
          >
            <Input />
          </Form.Item>
        </>
      ),
    },
  ];




  return (
    <>
      <Modal
        visible={visible}
        title={isEditMode ? "Edit Ledger" : "Add Ledger"}
        onCancel={onCancel}
        footer={null}
        width="95%"
        destroyOnClose
        maskClosable={false}
        centered
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Row gutter={16}>
            <Col span={16}>
              <Row gutter={16}>
                {/* First Name */}
                <Col span={12}>
                  <Form.Item name="ledger_first_name" label="First Name">
                    <Form.Item noStyle dependencies={["ledger_first_name"]}>
                      {({ getFieldValue, setFieldsValue }) => {
                        const currentValue = getFieldValue("ledger_first_name") || "";

                        const handleChange = (
                          e: React.ChangeEvent<HTMLInputElement>
                        ) => {
                          let value = e.target.value;

                          // केवल alphabets allow
                          value = value.replace(/[^a-zA-Z]/g, "");

                          // limit 50 chars
                          value = value.slice(0, 50);

                          // पहला अक्षर capital
                          if (value.length > 0) {
                            value = value.charAt(0).toUpperCase() + value.slice(1);
                          }

                          setFieldsValue({ ledger_first_name: value });
                        };

                        return (
                          <Input
                            value={currentValue}
                            maxLength={50}
                            onChange={handleChange}
                            suffix={
                              <span style={{ fontSize: "12px", color: "#888" }}>
                                {currentValue.length}/50
                              </span>
                            }
                          />
                        );
                      }}
                    </Form.Item>
                  </Form.Item>
                </Col>

                {/* Last Name */}
                <Col span={12}>
                    <Form.Item name="ledger_last_name" label="Last Name">
                      <Form.Item noStyle dependencies={["ledger_last_name"]}>
                        {({ getFieldValue, setFieldsValue }) => {
                          const currentValue = getFieldValue("ledger_last_name") || "";

                          const handleChange = (
                            e: React.ChangeEvent<HTMLInputElement>
                          ) => {
                            let value = e.target.value;

                            // केवल alphabets allow
                            value = value.replace(/[^a-zA-Z]/g, "");

                            // limit 50 chars
                            value = value.slice(0, 50);

                            // पहला अक्षर capital
                            if (value.length > 0) {
                              value = value.charAt(0).toUpperCase() + value.slice(1);
                            }

                            setFieldsValue({ ledger_last_name: value });
                          };

                          return (
                            <Input
                              value={currentValue}
                              maxLength={50}
                              onChange={handleChange}
                              suffix={
                                <span style={{ fontSize: "12px", color: "#888" }}>
                                  {currentValue.length}/50
                                </span>
                              }
                            />
                          );
                        }}
                      </Form.Item>
                    </Form.Item>
                </Col>

                {/* Mobile Number */}
                <Col span={12}>
                  {/* <Form.Item
                    name="ledger_mobile_no"
                    label="Mobile No"
                    rules={[
                      { required: true, message: "Required" },
                      {
                        pattern: /^[0-9]{10}$/,
                        message: "Mobile number must be 10 digits",
                      },
                    ]}
                  >
                    <Form.Item noStyle dependencies={["ledger_mobile_no"]}>
                      {({ getFieldValue, setFieldsValue }) => {
                        const currentValue = getFieldValue("ledger_mobile_no") || "";

                        const handleChange = (
                          e: React.ChangeEvent<HTMLInputElement>
                        ) => {
                          let value = e.target.value;

                          // केवल numbers allow
                          value = value.replace(/[^0-9]/g, "");

                          // limit 10 digits
                          value = value.slice(0, 10);

                          setFieldsValue({ ledger_mobile_no: value });
                        };

                        return (
                          <Input
                            value={currentValue}
                            maxLength={10}
                            type="tel"
                            onChange={handleChange}
                            suffix={
                              <span style={{ fontSize: "12px", color: "#888" }}>
                                {currentValue.length}/10
                              </span>
                            }
                          />
                        );
                      }}
                    </Form.Item>
                  </Form.Item> */}
                  <Form.Item
                  name="ledger_mobile_no"
                  label="Mobile No"
                  rules={[
                    { required: true, message: "Required" },
                    {
                      pattern: /^[0-9]{10}$/,
                      message: "Mobile number must be 10 digits",
                    },
                  ]}
                >
                  <Form.Item noStyle dependencies={["ledger_mobile_no"]}>
                    {({ getFieldValue, setFieldsValue }) => {
                      const currentValue = getFieldValue("ledger_mobile_no") || "";

                      const handleChange = (
                        e: React.ChangeEvent<HTMLInputElement>
                      ) => {
                        let value = e.target.value;

                        // Allow only digits
                        value = value.replace(/[^0-9]/g, "");

                        // Limit to 10 digits
                        value = value.slice(0, 10);

                        setFieldsValue({ ledger_mobile_no: value });
                      };

                      const handleBlur = () => {
                        const value = getFieldValue("ledger_mobile_no") || "";
                        if (!value) {
                          setFieldsValue({ ledger_mobile_no: "0123456789" });
                        }
                      };

                      return (
                        <Input
                          value={currentValue}
                          maxLength={10}
                          type="tel"
                          onChange={handleChange}
                          onBlur={handleBlur} // <-- Auto-fill here
                          suffix={
                            <span style={{ fontSize: "12px", color: "#888" }}>
                              {currentValue.length}/10
                            </span>
                          }
                        />
                      );
                    }}
                  </Form.Item>
                </Form.Item>

                </Col>
                {/* Account Head */}
                <Col span={12}>
                  <Form.Item name="ledger_account_head" label="Account Head">
                  <Select placeholder="Select head" allowClear>
                    {accountHeads.map((h) => (
                      <Option key={h.ah_id} value={h.ah_id}>
                        {h.ah_name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                </Col>

                {/* Account Sub Head */}
                <Col span={12}>
                  <Form.Item name="ledger_sub_account_head" label="Sub Head">
                    <Select placeholder="Select sub-head" allowClear>
                      {subHeads.map((sh) => (
                        <Option key={sh.ah_id} value={sh.ah_id}>
                          {sh.ah_name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                {/* Occupation */}
                <Col span={12}>
                  <Form.Item name="ledger_occupation" label="Occupation">
                    <Input />
                  </Form.Item>
                </Col>

              </Row>
            </Col>
            <Col span={8}>
            {/* ledger image */}
              <Row justify="center">
                 <Col>
                    <Form.Item label="Upload Ledger Image" required >
                       <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems:'center',
                          }}
                        >
                          <Upload
                              beforeUpload={() => false}
                              listType="picture-card"
                              maxCount={1}
                              fileList={profileImageFileList}
                              onPreview={handlePreview} // 👈 Add this
                              onChange={({ fileList }) => {
                                  setProfileImageFileList(fileList);
                                  const rawFile = fileList[0]?.originFileObj;
                                  if (rawFile instanceof File) {
                                      setImageFile(rawFile); // ✅ set real File here
                                  } else {
                                      setImageFile(null);
                                  }
                              }}
                          >
                              {profileImageFileList.length >= 1 ? null : uploadButton}
                          </Upload>
                        </div>
                      </Form.Item>
                 </Col>
              </Row>
            </Col>
  
              {/* designation */}
                <Col span={8}>
                  <Form.Item name="ledger_designation" label="Designation">
                    <Input />
                  </Form.Item>
                </Col>

                {/* Opening Balance */}
                <Col span={8}>
                  <Form.Item name="ledger_opening_balance" label="Opening Balance">
                    <Input type="number" />
                  </Form.Item>
                </Col>

            {/* Opening Type */}
            <Col span={8}>
              <Form.Item name="ledger_opening_type" label="Opening Type">
                <Select allowClear placeholder="Select type">
                  <Option value="Dr">Debit</Option>
                  <Option value="Cr">Credit</Option>
                </Select>
              </Form.Item>
            </Col>

            {/* Email */}
            <Col span={8}>
              <Form.Item name="ledger_email" label="Email">
                <Input />
              </Form.Item>
            </Col>

            {/* Company Name */}
            <Col span={8}>
              <Form.Item
                name="ledger_company_name"
                label="Company Name"
                rules={[
                  {
                    required: true,
                    message: "Company Name is required",
                  },
                  {
                    pattern: /^(?!\d+$).+$/, // sirf number hi na ho pura ka pura
                    message: "Company Name cannot be only numbers",
                  },
                ]}
              >
                <Form.Item noStyle dependencies={["ledger_company_name"]}>
                  {({ getFieldValue, setFieldsValue }) => {
                    const currentValue =
                      getFieldValue("ledger_company_name") || "";

                    const handleChange = (
                      e: React.ChangeEvent<HTMLInputElement>
                    ) => {
                      let value = e.target.value;

                      value = value.replace(/[0-9]/g, "");

                      // ✅ Max 50 chars
                      value = value.slice(0, 50);

                      // ✅ First letter uppercase
                      if (value.length > 0) {
                        value = value.charAt(0).toUpperCase() + value.slice(1);
                      }

                      setFieldsValue({ ledger_company_name: value });
                    };

                    return (
                      <Input
                        value={currentValue}
                        maxLength={50}
                        onChange={handleChange}
                        suffix={
                          <span style={{ fontSize: "12px", color: "#888" }}>
                            {currentValue.length}/50
                          </span>
                        }
                      />
                    );
                  }}
                </Form.Item>
              </Form.Item>
            </Col>

            {/* GST */}
            <Col span={8}>
              <Form.Item
                name="ledger_gstin"
                label="GSTIN"
                rules={[
                  {
                    pattern: /^[0-9A-Z]{15}$/,
                    message: "Enter valid 15-character GSTIN",
                  },
                ]}
                getValueFromEvent={(e) => {
                  let value = e.target.value;

                  // Sirf numbers aur alphabets allow karo
                  value = value.replace(/[^0-9a-zA-Z]/g, "");

                  // Auto-uppercase me convert karo
                  value = value.toUpperCase();

                  // Max 15 characters limit
                  if (value.length > 15) {
                    value = value.slice(0, 15);
                  }

                  return value;
                }}
              >
                <Input
                  placeholder="Enter GSTIN"
                  maxLength={15}
                  onChange={(e) => {
                    // yahan live counter update hoga
                    const counterEl = document.getElementById("gstin-counter");
                    if (counterEl) {
                      counterEl.innerText = `${e.target.value.length}/15`;
                    }
                  }}
                  suffix={
                    <span
                      id="gstin-counter"
                      style={{ fontSize: "12px", color: "#888" }}
                    >
                      0/15
                    </span>
                  }
                  onBlur={async (e) => {
                    const gstin = e.target.value.trim();
                    if (!gstin) return;

                    try {
                      const response = await apiRequest({
                        url: "check-gstin",
                        method: "get",
                        params: { gstIn: gstin },
                      });

                      if (response?.data) {
                        // valid GSTIN case
                      } else {
                        showSuccess(response?.message || "Found GSTIN");
                      }
                    } catch (err) {
                      showError("Failed to validate GSTIN");
                      console.error(err);
                    }
                  }}
                />
              </Form.Item>
            </Col>

            {/* Pan no.  */}
            <Col span={8}>
              <Form.Item
                name="ledger_pan_no"
                label="PAN No"
                rules={[
                  {
                    pattern: /^[A-Z0-9]{10}$/,
                    message: "Enter valid 10-character PAN No",
                  },
                ]}
                getValueFromEvent={(e) => {
                  let value = e.target.value;

                  // Sirf numbers aur alphabets allow karo
                  value = value.replace(/[^0-9a-zA-Z]/g, "");

                  // Auto-uppercase me convert karo
                  value = value.toUpperCase();

                  // Max 10 characters limit
                  if (value.length > 10) {
                    value = value.slice(0, 10);
                  }

                  return value;
                }}
              >
                <Input
                  placeholder="Enter PAN No"
                  maxLength={10}
                  onChange={(e) => {
                    // Live counter update
                    const counterEl = document.getElementById("pan-counter");
                    if (counterEl) {
                      counterEl.innerText = `${e.target.value.length}/10`;
                    }
                  }}
                  suffix={
                    <span
                      id="pan-counter"
                      style={{ fontSize: "12px", color: "#888" }}
                    >
                      0/10
                    </span>
                  }
                />
              </Form.Item>
            </Col>

            {/* Cin No */}
            <Col span={8}>
              <Form.Item
                name="ledger_cin_no"
                label="CIN No"
              >
                <Input
                  maxLength={21}
                  value={form.getFieldValue("ledger_cin_no")}
                  onChange={(e) => {
                    const onlyNums = e.target.value.replace(/\D/g, "");
                    form.setFieldsValue({ ledger_cin_no: onlyNums });

                    // Counter update
                    const counterEl = document.getElementById("cin-counter");
                    if (counterEl) {
                      counterEl.innerText = `${onlyNums.length}/21`;

                      // Limit reach hone par color red
                      counterEl.style.color =
                        onlyNums.length === 21 ? "red" : "#888";
                    }
                  }}
                  suffix={
                    <span
                      id="cin-counter"
                      style={{ fontSize: "12px", color: "#888" }}
                    >
                      0/21
                    </span>
                  }
                />
              </Form.Item>
            </Col>

            {/* Tin No */}
            <Col span={8}>
              <Form.Item
                name="ledger_tin_no"
                label="TIN No"
                rules={[
                  {
                    pattern: /^[0-9-]+$/, // ✅ sirf number aur hyphen allow
                    message: "TIN No must contain only numbers and hyphen (-)",
                  },
                ]}
              >
                <Input
                  maxLength={11}
                  value={form.getFieldValue("ledger_tin_no")}
                  onChange={(e) => {
                    const onlyValid = e.target.value.replace(/[^0-9-]/g, "");
                    form.setFieldsValue({ ledger_tin_no: onlyValid });

                    // Counter update
                    const counterEl = document.getElementById("tin-counter");
                    if (counterEl) {
                      counterEl.innerText = `${onlyValid.length}/11`;
                      counterEl.style.color =
                        onlyValid.length === 11 ? "red" : "#888";
                    }
                  }}
                  suffix={
                    <span
                      id="tin-counter"
                      style={{ fontSize: "12px", color: "#888" }}
                    >
                      0/11
                    </span>
                  }
                />
              </Form.Item>
            </Col>

            {/* MSME Teg no */}
            <Col span={8}>
              <Form.Item
                name="ledger_msme_reg_no"
                label="MSME Reg No"
              >
                <Input
                  maxLength={19}
                  value={form.getFieldValue("ledger_msme_reg_no")}
                  onChange={(e) => {
                    const onlyNums = e.target.value.replace(/\D/g, "");
                    form.setFieldsValue({ ledger_msme_reg_no: onlyNums });

                    // Counter update
                    const counterEl = document.getElementById("msme-counter");
                    if (counterEl) {
                      counterEl.innerText = `${onlyNums.length}/19`;
                      counterEl.style.color =
                        onlyNums.length === 19 ? "red" : "#888";
                    }
                  }}
                  suffix={
                    <span
                      id="msme-counter"
                      style={{ fontSize: "12px", color: "#888" }}
                    >
                      0/19
                    </span>
                  }
                />
              </Form.Item>
            </Col>

            {/* Bill to bill */}
            <Col span={8}>
              <Form.Item name="ledger_bill_to_bill" label="Bill To Bill">
                <Input />
              </Form.Item>
            </Col>

            {/* Deals in COmpnay */}
            <Col span={8}>
              <Form.Item name="ledger_deals_in_company" label="Deals In Company">
                <Select
                  className="input-bordor-radius-with-border "
                  style={{ width: "100%" }}
                  placeholder="Select comapny name"
                  allowClear
                  mode="multiple"
                  showSearch
                >
                  {brandnames.map((bn) => (
                    <Option key={bn.brand_id} value={bn.brand_id}>
                      {bn.brand_name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            {/* Deals in products */}
            <Col span={8}>
              <Form.Item name="ledger_deals_in_product" label="Deals In Product">
                <Select
                  className="input-bordor-radius-with-border"
                  style={{ width: "100%" }}
                  placeholder="Select comapny name"
                  allowClear
                  mode="multiple"
                  showSearch
                >
                  {product_names.map((pn) => (
                    <Option key={pn.pn_id} value={pn.pn_id}>
                      {pn.pn_name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            {/* Intrest rate */}
            <Col span={8}>
              <Form.Item name="ledger_interest_rate" label="Interest Rate">
                <Input type="number" />
              </Form.Item>
            </Col>

            {/* Intrest Style */}
            <Col span={8}>
              <Form.Item name="ledger_interest_style" label="Interest Style">
                <Select allowClear placeholder="Select style">
                  <Option value="simple">Simple</Option>
                  <Option value="compound">Compound</Option>
                </Select>
              </Form.Item>
            </Col>

            {/* Credit Limit Day */}
            <Col span={8}>
              <Form.Item name="ledger_credit_limit_day" label="Credit Limit Day">
                <Input type="number" />
              </Form.Item>
            </Col>

            {/*  Credit Limit Amount */}
            <Col span={8}>
              <Form.Item
                name="ledger_credit_limit_amount"
                label="Credit Limit Amount"
              >
                <Input type="number" />
              </Form.Item>
            </Col>

            {/*  Date of Birth */}
            <Col span={8}>
              <Form.Item
                name="ledger_dob"
                label="Date of Birth"
                rules={[{ required: true, message: "Date of Birth is required" }]}
              >
                <DatePicker
                  style={{
                    width: "100%",
                    borderRadius: "20px",
                    height: "45px",
                  }}
                  format="DD-MM-YYYY"
                  inputReadOnly // user input disable
                  onFocus={(e) => e.target.blur()} // keyboard typing block
                />
              </Form.Item>
            </Col>

            {/* Marriage Anniversary */}
            <Col span={8}>
              <Form.Item
                name="ledger_marriage_anniversary"
                label="Marriage Anniversary"
              >
                <DatePicker
                  style={{
                    width: "100%",
                    borderRadius: "20px",
                    height: "45px",
                  }}
                  format="DD-MM-YYYY"
                  inputReadOnly // typing disable
                  onFocus={(e) => e.target.blur()} // sirf calendar se date select hogi
                />
              </Form.Item>
            </Col>

            {/* Work Anniversary  */}
            <Col span={8}>
              <Form.Item name="ledger_work_anniversary" label="Work Anniversary">
                <DatePicker
                  style={{
                    width: "100%",
                    borderRadius: "20px",
                    height: "45px",
                  }}
                />
              </Form.Item>
            </Col>

            {/* Offer Festival */}
            <Col span={8}>
              <Form.Item
                name="ledger_offer_festival"
                label="Offer Festival"
              >
                <Select placeholder="Select Festival">
                  <Select.Option value="diwali">Diwali</Select.Option>
                  <Select.Option value="holi">Holi</Select.Option>
                  <Select.Option value="eid">Eid</Select.Option>
                  <Select.Option value="christmas">Christmas</Select.Option>
                  <Select.Option value="pongal">Pongal</Select.Option>
                </Select>
              </Form.Item>
            </Col>

            {/* ledger_status */}
            <Col span={8}>
              <Form.Item name="ledger_status" label="Status">
                <Select allowClear placeholder="Select status">
                  <Option value={1}>Active</Option>
                  <Option value={0}>Inactive</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* Links */}
          <Form.List name="links">
            {(fields, { add, remove }) => (
              <div
                style={{
                  background: "#f5f5f5", // full background for whole block
                  padding: "16px",
                  borderRadius: "6px",
                  marginBottom: "24px",
                }}
              >
                {/* Header + Add button aligned right */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                    marginBottom: 12,
                  }}
                >
                  <h4 style={{ marginBottom: 0 }}>Links</h4>
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    icon={<PlusOutlined />}
                    size="middle"
                  >
                    Add link
                  </Button>
                </div>

                {/* All link entries */}
                {fields.map(({ key, name, ...rest }) => (
                  <Space
                    key={key}
                    align="baseline"
                    style={{
                      display: "flex",
                      marginBottom: 8,
                    }}
                  >
                    {/* Link or name */}
                    <Form.Item
                      {...rest}
                      name={[name, "link"]}
                      label="Link or Name"
                      style={{ marginBottom: 0 }}
                      rules={[{ required: true, message: "Link is required" }]}
                    >
                      <Input placeholder="Link or name" />
                    </Form.Item>

                    {/* Description */}
                    <Form.Item
                      {...rest}
                      name={[name, "description"]}
                      label="Description"
                      style={{ marginBottom: 0 }}
                    >
                      <Input placeholder="Description" />
                    </Form.Item>

                    <MinusCircleOutlined onClick={() => remove(name)} />
                  </Space>
                ))}
              </div>
            )}
          </Form.List>


          {/* Banks section */}
          <Form.List name="banks">
            {(fields, { add, remove }) => (
              <div
                style={{
                  background: "#f5f5f5", // background color for entire block
                  padding: "16px",
                  borderRadius: "8px",
                  marginBottom: "24px",
                  // border: '1px solid #e0e0e0',
                }}
              >
                {/* Heading + Add Button aligned right */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                    marginBottom: 12,
                  }}
                >
                  <h4 style={{ marginBottom: 0 }}>Bank Details</h4>
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    icon={<PlusOutlined />}
                    size="middle"
                  >
                    Add bank
                  </Button>
                </div>

                {/* All bank fields */}
                {fields.map(({ key, name, ...rest }) => (
                  <Space
                    key={key}
                    align="baseline"
                    style={{ display: "flex", marginBottom: 8 }}
                  >
                    {/* IFSC */}
                    <Form.Item
                      {...rest}
                      name={[name, "ifsc"]}
                      label="IFSC Code"
                      style={{ marginBottom: 0 }}
                      rules={[{ required: true, message: "IFSC required" }]}
                    >
                      <Input
                        placeholder="Enter IFSC code"
                        maxLength={11}
                        onBlur={async (e) => {
                          const ifsc = e.target.value.trim().toUpperCase(); // IFSCs are uppercase
                          if (!ifsc) return;

                          try {
                            const response = await apiRequest({
                              url: "check-ifsc",
                              method: "get",
                              params: { ifsc }, // sent as query param: ?ifsc=XXXX
                            });

                            if (response?.data) {
                              const index =
                                typeof name === "string"
                                  ? parseInt(name, 10)
                                  : name;
                              form.setFields([
                                {
                                  name: ["banks", index, "bank_name"],
                                  value: response.data.bankName,
                                },
                                {
                                  name: ["banks", index, "address"],
                                  value: response.data.address,
                                },
                              ]);

                              console.log("name:", name, typeof name);

                              console.log("IFSC valid:", response.data);
                            } else {
                              showError(response?.message || "Invalid IFSC");
                            }
                          } catch (err) {
                            showError("Failed to validate IFSC");
                            console.error(err);
                          }
                        }}
                      />
                    </Form.Item>

                    {/* Bank name  */}
                    <Form.Item
                      {...rest}
                      name={[name, "bank_name"]}
                      label="Bank Name"
                      style={{ marginBottom: 0 }}
                      rules={[{ required: true, message: "Bank name required" }]}
                    >
                      <Input placeholder="Bank name" />
                    </Form.Item>

                    {/* Bank Address */}
                    <Form.Item
                      {...rest}
                      label="Bank Address"
                      name={[name, "address"]}
                      style={{ marginBottom: 0 }}
                    >
                      <Input placeholder="Bank address" />
                    </Form.Item>

                    {/* Account no */}
                    <Form.Item
                    {...rest}
                    name={[name, "acc_no"]}
                    label="Account Number"
                    style={{ marginBottom: 0 }}
                    rules={[
                      { required: true, message: "Account No required" },
                      {
                        validator: (_, value) => {
                          if (!value) return Promise.resolve();

                          const accountNumberRegex = /^[0-9]{9,18}$/;

                          if (!accountNumberRegex.test(value)) {
                            return Promise.reject(
                              new Error("Account number must be 9 to 18 digits (only numbers)")
                            );
                          }

                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Input placeholder="Account number" />
                  </Form.Item>


                    {/* Holder Name */}
                    <Form.Item
                      {...rest}
                      name={[name, "holder_name"]}
                      style={{ marginBottom: 0 }}
                      label="Holder Name"
                    >
                      <Input placeholder="Holder Name" />
                    </Form.Item>

                    <MinusCircleOutlined onClick={() => remove(name)} />
                  </Space>
                ))}
              </div>
            )}
          </Form.List>

          {/* Address */}
          <Form.List name="addresses">
            {(fields, { add, remove }) => (
              <div
                style={{
                  background: "#f5f5f5",
                  padding: "16px",
                  borderRadius: "8px",
                  marginBottom: "24px",
                }}
              >
                {/* Header and Add Button aligned */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                    marginBottom: 12,
                  }}
                >
                  <h4 style={{ marginBottom: 0 }}>Fixed Addresses</h4>
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    icon={<PlusOutlined />}
                    size="middle"
                  >
                    Add address
                  </Button>
                </div>

                {fields.map(({ key, name, ...rest }) => (
                  <div
                    key={key}
                    style={{
                      marginBottom: 16,
                      padding: 16,
                      borderRadius: 6,
                      background: "#f5f5f5", 
                    }}
                  >
                    <Row gutter={8}>
                      {/* Address Type  */}
                      <Col span={6}>
                        <Form.Item
                          {...rest}
                          name={[name, "type"]}
                          label="Type"
                          style={{ marginBottom: 8, backgroundColor: "#f5f5f5" }}
                        >
                          <Select
                            style={{ backgroundColor: "#f5f5f5" }}
                            dropdownStyle={{ backgroundColor: "#f5f5f5" }}
                            popupClassName="custom-select-dropdown"
                            className="custom-select"
                          >
                            <Option value="residence">Residence</Option>
                            <Option value="office">Office</Option>
                            <Option value="postal">Postal</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      
                      {/* Address */}
                      <Col span={18}>
                        <Form.Item
                          {...rest}
                          name={[name, "address"]}
                          label="Address"
                          style={{ marginBottom: 8 }}
                        >
                          <Input placeholder="Full address" />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={8}>
                      {/* Landmark */}
                      <Col span={6}>
                        <Form.Item
                          {...rest}
                          name={[name, "landmark"]}
                          label="Landmark"
                          style={{ marginBottom: 8 }}
                        >
                          <Input />
                        </Form.Item>
                      </Col>
                      
                      {/* Pincode */}
                      <Col span={6}>
                        <Form.Item
                          {...rest}
                          name={[name, "pincode"]}
                          label="Pincode"
                          style={{ marginBottom: 8 }}
                        >
                          <Input
                            maxLength={6}
                            onBlur={(e) =>
                              handlePincodeBlur(e.target.value, name)
                            }
                          />
                        </Form.Item>
                      </Col>

                      {/* City */}
                      <Col span={6}>
                        <Form.Item
                          {...rest}
                          name={[name, "city"]}
                          label="City"
                          style={{ marginBottom: 8 }}
                        >
                          <Input />
                        </Form.Item>
                      </Col>

                      {/* State */}
                      <Col span={6}>
                        <Form.Item
                          {...rest}
                          name={[name, "state"]}
                          label="State"
                          style={{ marginBottom: 8 }}
                        >
                          <Input />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={8} align="middle">
                      
                      {/* Country */}
                      <Col span={12}>
                        <Form.Item
                          {...rest}
                          name={[name, "country"]}
                          label="Country"
                          style={{ marginBottom: 0 }}
                        >
                          <Input />
                        </Form.Item>
                      </Col>

                      <Col span={12} style={{ textAlign: "right" }}>
                        <Button
                          danger
                          icon={<MinusCircleOutlined />}
                          onClick={() => remove(name)}
                        >
                          Remove address
                        </Button>
                      </Col>
                    </Row>
                  </div>
                ))}
              </div>
            )}
          </Form.List>

          {/* Phones */}
        <Form.List name="phones">
            {(fields, { add, remove }) => (
              <div
                style={{
                  background: "#f5f5f5",
                  padding: "16px",
                  borderRadius: "6px",
                  marginBottom: "24px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                    marginBottom: 12,
                  }}
                >
                  <h4 style={{ marginBottom: 0 }}>Contact Details</h4>
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    icon={<PlusOutlined />}
                    size="middle"
                  >
                    Add contact
                  </Button>
                </div>

                {fields.map(({ key, name, ...rest }) => (
                  <Space
                    key={key}
                    align="baseline"
                    style={{ display: "flex", marginBottom: 8 }}
                  >
                    <Row gutter={16}>
                      {/* First Name */}
                      <Col span={8}>
                        <Form.Item
                          {...rest}
                          name={[name, "first_name"]}
                          label="First Name"
                          rules={[{ required: true, message: "First name required" }]}
                        >
                          <Input placeholder="First name" />
                        </Form.Item>
                      </Col>

                      {/* Last Name */}
                      <Col span={8}>
                        <Form.Item {...rest} 
                        label="Last Name"
                        name={[name, "last_name"]}>
                          <Input placeholder="Last name" />
                        </Form.Item>
                      </Col>

                      {/* Designation */}
                      <Col span={8}>
                        <Form.Item {...rest}
                        label="Designation"
                        name={[name, "designation"]}>
                          <Input placeholder="Designation" />
                        </Form.Item>
                      </Col>

                      {/* Email */}
                      <Col span={8}>
                        <Form.Item {...rest} 
                          label="Email"
                          name={[name, "email"]}>
                          <Input placeholder="Email" />
                        </Form.Item>
                      </Col>

                      {/* Label */}
                      <Col span={8}>
                        <Form.Item {...rest} name={[name, "label"]}
                        label="Label"
                        >
                          <Input placeholder="Label" />
                        </Form.Item>
                      </Col>

                      {/* Contact Type */}
                      <Col span={8}>
                        <Form.Item
                          {...rest}
                          name={[name, "type"]}
                          label="Contact Type"
                          rules={[{ required: true, message: "Select type" }]}
                        >
                          <Select placeholder="Select Contact Type">
                            <Select.Option value="mobile">Mobile</Select.Option>
                            <Select.Option value="phone">Phone</Select.Option>
                          </Select>
                        </Form.Item>
                      </Col>

                      {/* Conditional Fields */}
                      <Form.Item
                        noStyle
                        shouldUpdate={(prev, next) =>
                          prev.phones?.[name]?.type !== next.phones?.[name]?.type
                        }
                      >
                        {({ getFieldValue, setFieldsValue }) => {
                          const type = getFieldValue(["phones", name, "type"]);

                          if (type === "mobile") {
                            return (
                              <Col span={8}>
                                <Form.Item
                                  {...rest}
                                  name={[name, "number"]}
                                  label="Mobile Number"
                                  rules={[
                                    { required: true, message: "Mobile number required" },
                                    { pattern: /^[0-9]{10}$/, message: "Must be 10 digits" },
                                  ]}
                                >
                                  <Input
                                    placeholder="Mobile Number"
                                    maxLength={10}
                                    onChange={(e) => {
                                      const cleaned = e.target.value.replace(/\D/g, "");
                                      setFieldsValue({
                                        phones: {
                                          [name]: {
                                            number: cleaned,
                                            phone_no: undefined,
                                            std_code: undefined,
                                          },
                                        },
                                      });
                                    }}
                                  />
                                </Form.Item>
                              </Col>
                            );
                          }

                          if (type === "phone") {
                            const stdCode = getFieldValue(["phones", name, "std_code"]) || "";
                            const phonePart = getFieldValue(["phones", name, "phone_part"]) || "";

                            return (
                              <>
                                {/* STD Code */}
                                <Col span={8}>
                                  <Form.Item
                                    {...rest}
                                    name={[name, "std_code"]}
                                    label="STD Code"
                                    rules={[
                                      { required: true, message: "STD code required" },
                                      { pattern: /^[0-9]{1,5}$/, message: "Max 5 digits" },
                                    ]}
                                  >
                                    <Input
                                      placeholder="STD"
                                      maxLength={5}
                                      onChange={(e) => {
                                        const cleaned = e.target.value.replace(/\D/g, "");
                                        const updatedPhone = cleaned + phonePart;
                                        setFieldsValue({
                                          phones: {
                                            [name]: {
                                              std_code: cleaned,
                                              phone_part: phonePart,
                                              phone_no: updatedPhone,
                                              number: undefined,
                                            },
                                          },
                                        });
                                      }}
                                    />
                                  </Form.Item>
                                </Col>

                                {/* Phone Number */}
                                <Col span={8}>
                                  <Form.Item
                                  {...rest}
                                  name={[name, "std_phone"]}
                                  label="Phone Number"
                                  rules={[
                                    { required: true, message: "STD phone required" },
                                    { pattern: /^[0-9]{6,10}$/, message: "6–10 digits" },
                                  ]}
                                >
                                  <Input
                                    placeholder="STD Phone"
                                    maxLength={10}
                                    onChange={(e) => {
                                      const cleaned = e.target.value.replace(/\D/g, "");
                                      setFieldsValue({
                                        phones: {
                                          [name]: {
                                            std_phone: cleaned,
                                            number: undefined,
                                            phone_no: undefined,
                                          },
                                        },
                                      });
                                    }}
                                  />
                                </Form.Item>

                                </Col>

                                {/* Hidden phone_no for backend */}
                                <Form.Item name={[name, "phone_no"]} hidden>
                                  <Input type="hidden" />
                                </Form.Item>
                              </>
                            );
                          }

                          return null;
                        }}
                      </Form.Item>
                    </Row>
                       

                    {/* Remove button */}
                    <MinusCircleOutlined onClick={() => remove(name)} />
                  </Space>
                ))}
              </div>
            )}
          </Form.List>

          {/* documents */}
          <Form.List name="documents">
            {(fields, { add, remove }) => (
              <div
                style={{
                  background: "#f5f5f5",
                  padding: "16px",
                  borderRadius: "6px",
                  marginBottom: "24px",
                }}
              >
                {/* Header + Add button */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                    marginBottom: 12,
                  }}
                >
                  <h4 style={{ marginBottom: 0 }}>Documents</h4>
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    icon={<PlusOutlined />}
                    size="middle"
                  >
                    Add document
                  </Button>
                </div>

                {fields.map(({ key, name, ...rest }) => (
                  <Row gutter={16}>
                    {/* Document Type */}
                    <Col span={8}>
                      <Form.Item
                        name={[name, "document_type"]}
                        label="Document Type"
                        rules={[
                          {
                            required: true,
                            message: "Please select document type",
                          },
                        ]}
                      >
                        <Select allowClear placeholder="Select document type">
                          <Option value="aadhaar">Aadhaar Card</Option>
                          <Option value="pan">PAN Card</Option>
                          <Option value="10th">10th Certificate</Option>
                          <Option value="12th">12th Certificate</Option>
                          <Option value="graduation">
                            Graduation Certificate
                          </Option>
                          <Option value="other">Other</Option>
                        </Select>
                      </Form.Item>
                    </Col>

                    {/* Document Name */}
                    <Col span={8}>
                      <Form.Item shouldUpdate noStyle>
                        {({ getFieldValue, setFieldsValue }) => {
                          const value =
                            getFieldValue([name, "document_name"]) || "";

                          return (
                            <Form.Item
                              {...rest}
                              name={[name, "document_name"]}
                              label="Document Name"
                              rules={[
                                {
                                  required: true,
                                  message: "Document name is required",
                                },
                                {
                                 pattern: /^[A-Za-z0-9]+$/,
                                  message: "Only alphabets are allowed",
                                },
                              ]}
                            >
                              <Input
                                placeholder="Document Name"
                                maxLength={25}
                                showCount
                                value={value}
                                onChange={(e) => {
                                  // Sirf alphabets allow karo (space, numbers, special chars remove)
                                   let onlyAlphaNum = e.target.value.replace(/[^A-Za-z0-9]/g, "");

                                  // First letter capitalize
                                    if (onlyAlphaNum.length > 0) {
                                      onlyAlphaNum =
                                        onlyAlphaNum.charAt(0).toUpperCase() +
                                        onlyAlphaNum.slice(1);
                                    }

                                  // Field value update karo
                                  setFieldsValue({
                                    documents: {
                                      [name]: {
                                        document_name: onlyAlphaNum,
                                      },
                                    },
                                  });
                                }}
                              />
                            </Form.Item>
                          );
                        }}
                      </Form.Item>
                    </Col>

                    {/* Upload Document */}
                    <Col span={8}>
                      <Form.Item
                        name={[name, "attachments"]}
                        label="Upload Document (PDF only)"
                        rules={[
                          {
                            required: true,
                            message: "Please upload a document",
                          },
                        ]}
                        valuePropName="fileList"
                        getValueFromEvent={(e) =>
                          Array.isArray(e) ? e : e?.fileList
                        }
                      >
                        <Upload
                          beforeUpload={(file) => {
                            const isPdf = file.type === "application/pdf";
                            if (!isPdf) {
                              Modal.error({
                                title: "Invalid File Type",
                                content: "Only PDF files are allowed!",
                              });
                              return Upload.LIST_IGNORE;
                            }
                            return false; // prevent auto-upload
                          }}
                          maxCount={1}
                          style={{ width: "80%" }}
                        >
                          <Button
                            icon={<UploadOutlined />}
                            block
                            style={{
                              height: "80px",
                              fontSize: "16px",
                            }}
                          >
                            Upload PDF
                          </Button>
                        </Upload>
                      </Form.Item>
                    </Col>

                    {/* Remove Button */}
                    <MinusCircleOutlined onClick={() => remove(name)} />
                  </Row>
                ))}
              </div>
            )}
          </Form.List>


          <Table
            rowKey="category_id"
            columns={columns}
            dataSource={categories}
            pagination={false}
          />

          <Form.Item>
            <Button type="primary" htmlType="submit" style={{ marginTop: 16 }}>
              {isEditMode ? "Update" : "Add"}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        visible={previewOpen}
        title={previewTitle}
        footer={null}
        centered
        onCancel={() => setPreviewOpen(false)}
    >
        <img
            alt="Preview"
            style={{ width: '100%' }}
            src={previewImage}
    />
    </Modal>
  </>
  );
};

export default LedgerModal;
